import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import { cn, toTitleCase } from '@/lib/utils'
import { useEffect, useId, useState, type ReactNode } from 'react'
import { Badge } from '../ui/badge'
import { Label } from '../ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type SelectProps,
  type SelectTriggerSize,
} from '../ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  type TooltipContentProps,
  type TooltipContentVariant,
} from '../ui/tooltip'
import FieldInfo from './FieldInfo'

export type SelectInputOptionId = string | number | boolean | bigint
export type SelectInputValue = SelectInputOptionId | null | undefined

export type SelectInputOption = {
  id: SelectInputOptionId
  name?: ReactNode
  description?: ReactNode
  start?: ReactNode
  end?: ReactNode
  disabled?: boolean
  tooltip?: ReactNode
  tooltipAnchor?: 'end'
  tooltipSide?: TooltipContentProps['side']
  tooltipSideOffset?: number
  tooltipAlign?: TooltipContentProps['align']
  tooltipAlignOffset?: number
  tooltipClassName?: string
  tooltipVariant?: TooltipContentVariant
}

const hasValue = (value: SelectInputValue): value is SelectInputOptionId =>
  value !== '' && value !== null && typeof value !== 'undefined'

const toSelectValue = (value: SelectInputValue): string => {
  if (!hasValue(value)) {
    return ''
  }

  return String(value)
}

const findMatchingOption = (
  options: readonly SelectInputOption[],
  value: SelectInputValue
): SelectInputOption | null => {
  const normalizedValue = toSelectValue(value)
  if (!normalizedValue) {
    return null
  }

  return options.find((option) => toSelectValue(option?.id) === normalizedValue) ?? null
}

const renderOptionLabel = (option: SelectInputOption, end: ReactNode = option?.end) => {
  const label = (
    <span className="flex min-w-0 items-center gap-2">
      {option?.start}
      <span className="min-w-0 truncate">{option?.name}</span>
      {end}
    </span>
  )

  if (!option?.description) {
    return label
  }

  return (
    <div className="flex min-w-0 flex-col items-start text-left">
      <span className="max-w-full font-medium">{label}</span>
      <span className="max-w-full truncate text-xs leading-4 text-muted-foreground">
        {option.description}
      </span>
    </div>
  )
}

const coerceValueToCurrentType = (
  value: string,
  currentValue: SelectInputValue
): SelectInputOptionId => {
  if (typeof currentValue === 'number') {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : value
  }

  if (typeof currentValue === 'boolean') {
    return value === 'true'
  }

  if (typeof currentValue === 'bigint') {
    try {
      return BigInt(value)
    } catch {
      return value
    }
  }

  return value
}

type SelectOptionProps = {
  option: SelectInputOption
  selectItemClassName?: string
}

function SelectOption({ option, selectItemClassName }: SelectOptionProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const anchorTooltipToEnd =
    option.tooltipAnchor === 'end' && Boolean(option.tooltip) && Boolean(option.end)
  const tooltipContent = option.tooltip ? (
    <TooltipContent
      side={option.tooltipSide ?? 'top'}
      sideOffset={option.tooltipSideOffset}
      align={option.tooltipAlign}
      alignOffset={option.tooltipAlignOffset}
      className={cn(option.tooltipClassName, anchorTooltipToEnd && 'pointer-events-none')}
      style={anchorTooltipToEnd ? { pointerEvents: 'none' } : undefined}
      variant={option.tooltipVariant}
    >
      {option.tooltip}
    </TooltipContent>
  ) : null
  const end = anchorTooltipToEnd ? (
    <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
      <TooltipTrigger asChild>
        <span className="inline-flex">{option.end}</span>
      </TooltipTrigger>
      {tooltipContent}
    </Tooltip>
  ) : (
    option.end
  )
  const item = (
    <SelectItem
      align={option.description ? 'start' : 'center'}
      size={option.description ? 'comfortable' : 'default'}
      className={selectItemClassName}
      value={String(option.id)}
      disabled={option.disabled}
      {...(anchorTooltipToEnd
        ? {
            onPointerEnter: () => setTooltipOpen(true),
            onPointerLeave: () => setTooltipOpen(false),
            onFocus: () => setTooltipOpen(true),
            onBlur: () => setTooltipOpen(false),
          }
        : {})}
    >
      {renderOptionLabel(option, end)}
    </SelectItem>
  )

  if (!option.tooltip || anchorTooltipToEnd) {
    return item
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{item}</TooltipTrigger>
      {tooltipContent}
    </Tooltip>
  )
}

export type SelectInputClassNames = {
  select?: string
  selectTrigger?: string
  selectContent?: string
  selectItem?: string
}

type SelectInputOwnProps = {
  id?: string
  label?: ReactNode
  ariaLabel?: string | null
  options?: readonly SelectInputOption[]
  labelEnd?: ReactNode
  onValueChange?: (value: SelectInputOptionId) => void
  placeholder?: ReactNode
  className?: string
  classNames?: SelectInputClassNames
  readOnlyEmptyText?: string
  formatReadOnlyValue?: (value: string) => ReactNode
  disabled?: boolean
  disabledTooltip?: ReactNode
  size?: SelectTriggerSize
}

export type SelectInputProps = SelectInputOwnProps &
  Omit<SelectProps, keyof SelectInputOwnProps | 'value' | 'onValueChange'>

export default function SelectInput({
  id,
  label = null,
  ariaLabel = null,
  options = [],
  labelEnd = null,
  onValueChange,
  placeholder = '',
  className = '',
  classNames = {
    select: '',
    selectTrigger: '',
    selectContent: '',
    selectItem: '',
  },
  readOnlyEmptyText = 'Not selected',
  formatReadOnlyValue,
  disabled = false,
  disabledTooltip = null,
  size = 'default',
  ...props
}: SelectInputProps) {
  const field = useFieldContext<SelectInputValue>()
  const { isReadOnly } = useIsReadOnly()
  const currentValue = field?.state?.value
  const randomId = useId()
  const triggerId = id || `${randomId}-${field.name}`
  const matchingOption = findMatchingOption(options, currentValue)

  const matchingOptionName = matchingOption?.name
  const optionName =
    typeof matchingOptionName === 'string'
      ? matchingOptionName
      : hasValue(currentValue)
        ? String(currentValue)
        : ''

  const displayValue = optionName || readOnlyEmptyText
  const selectValue = toSelectValue(currentValue)
  const showDisabledTooltip = disabled && Boolean(disabledTooltip)
  const getNextValue = (id: string): SelectInputOptionId | undefined => {
    // -1 is the "loading" state
    if (id === '-1') {
      return undefined
    }

    // Keep values in the same type family as their defaults so form dirty-state
    // can return to "clean" when the user re-selects the original option.
    if (typeof currentValue === 'string' && currentValue !== '') {
      return id
    }

    const matchingOption = findMatchingOption(options, id)

    if (matchingOption) {
      if (hasValue(currentValue)) {
        return coerceValueToCurrentType(id, currentValue)
      }

      return matchingOption.id
    }

    return coerceValueToCurrentType(id, currentValue)
  }

  // If the list of options is updated, and the current value no longer exists, clear the value
  useEffect(() => {
    if (hasValue(field.state.value) && !findMatchingOption(options, field.state.value)) {
      field.handleChange('')
    }
  }, [field, options])

  const selectTrigger = (
    <SelectTrigger
      id={triggerId}
      aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
      size={size}
      className={cn(
        'w-full',
        showDisabledTooltip && 'pointer-events-none',
        classNames.selectTrigger
      )}
    >
      {matchingOption?.description ? (
        <div className="flex min-w-0 flex-1 flex-col items-start text-left">
          <span className="max-w-full truncate font-medium">{optionName}</span>
          <span className="max-w-full truncate text-xs leading-4 text-muted-foreground">
            {matchingOption.description}
          </span>
        </div>
      ) : (
        <SelectValue placeholder={placeholder} />
      )}
    </SelectTrigger>
  )

  return (
    <div className={cn('flex flex-col gap-y-2', className)}>
      {label && (
        <div className="inline-flex items-center">
          <Label className="" htmlFor={triggerId}>
            {label}
          </Label>
          {labelEnd}
        </div>
      )}

      {isReadOnly ? (
        <Badge size="md" variant="brand">
          {(formatReadOnlyValue ?? toTitleCase)(displayValue)}
        </Badge>
      ) : (
        <>
          <Select
            value={selectValue}
            onValueChange={(id) => {
              const nextValue = getNextValue(id)

              if (typeof nextValue === 'undefined') {
                return
              }

              field.handleChange(nextValue)
              onValueChange?.(nextValue)
            }}
            disabled={disabled}
            {...props}
          >
            {showDisabledTooltip ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    aria-label={
                      typeof label === 'string' ? `${label} unavailable` : 'Unavailable selection'
                    }
                    className="block w-full cursor-not-allowed"
                    tabIndex={0}
                  >
                    {selectTrigger}
                  </span>
                </TooltipTrigger>
                <TooltipContent sideOffset={4}>{disabledTooltip}</TooltipContent>
              </Tooltip>
            ) : (
              selectTrigger
            )}
            <SelectContent className={classNames.selectContent}>
              {options.map((option) => (
                <SelectOption
                  key={String(option.id)}
                  option={option}
                  selectItemClassName={classNames.selectItem}
                />
              ))}
            </SelectContent>
          </Select>
          <FieldInfo />
        </>
      )}
    </div>
  )
}
